import { Component, OnInit } from "@angular/core";
import { NbDialogRef } from "@nebular/theme";
import { EnvironmentService } from "@services/environment.service";
import { HttpUtilService } from "@services/http-util.service";
import { NotificationsService } from "@services/notifications.service";
import { UserService } from "@services/user.service";
import { of } from "rxjs";
import { catchError, take, tap } from "rxjs/operators";

@Component({
  selector: "app-mfa-modal",
  templateUrl: "./mfa-modal.component.html",
  styleUrls: ["./mfa-modal.component.scss"],
})
export class MfaModalComponent implements OnInit {
  public isLoading: boolean;
  public isSubmitting: boolean;
  public secretCode: string;
  public totbCode: string;
  public smsCode: string;
  public showInitalAsk: boolean = true;
  public showMfaOptions: boolean = false;
  public totbVerified: boolean = false;
  public smsVerified: boolean = false;
  public phoneNumberVerified: boolean = false;
  public userInfo;
  public userInfo$;
  public view: "initial" | "mfa" | "sms" | "options" = "initial";
  public phoneNumberUsed;

  constructor(
    private environmentService: EnvironmentService,
    public dialogRef: NbDialogRef<MfaModalComponent>,
    private http: HttpUtilService,
    public notifications: NotificationsService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    if (this?.["data"]?.initialView) this.view = this["data"].initialView;
    this.userInfo$ = this.userService
      .getRoleDetails()
      .pipe(catchError((error) => of({ error })))
      .pipe(
        tap((role: any) => {
          if (role?.data) {
            this.userInfo = role.data;
          }
        })
      );
  }

  close() {
    this.dialogRef.close();
  }

  showMfaSetup() {
    this.view = "options";
  }

  initSmsMfaSetup() {
    const e164Regex = /^\+?[1-9]\d{1,14}$/;
    if (!this.userInfo?.phone_number?.e164Number) {
      return this.notifications.popNotification(
        "",
        "Must have a phone number setup. This can be provided from the profile page"
      );
    }
    if (!e164Regex.test(this.userInfo?.phone_number?.e164Number)) {
      return this.notifications.popNotification(
        "",
        "Must have a valid phone number setup. This can be edited from the profile page"
      );
    }
    this.userService
      .getVerificationCode("phone_number")
      .pipe(catchError((error) => of({ error })))
      .subscribe((response) => {
        if (!response || response?.error) {
          const message =
            response.error === "LimitExceededException"
              ? "Too many attemps made at this time. Please try again later"
              : "Unknown error getting verification code";
          this.notifications.popNotification("", message);
          this.isLoading = false;
          return;
        }
        this.secretCode = response.SecretCode;
        this.phoneNumberUsed =
          response?.details?.CodeDeliveryDetails?.Destination || "";
        this.view = "sms";
        this.isLoading = false;
      });
  }

  initTotbMfaSetup() {
    this.http
      .request(
        "get",
        `${this.environmentService.environment.apiRoot}/auth/inittotbmfa`
      )
      .pipe(catchError((error) => of({ error })))
      .subscribe((response) => {
        this.secretCode = response.SecretCode;
        this.view = "mfa";
        this.isLoading = false;
      });
  }

  async handleSuccessfulSmsVerifyResponse() {
    this.http
      .request(
        "post",
        `${this.environmentService.environment.apiRoot}/auth/updatemfa`,
        {
          username: this.userInfo?.cognito_username,
          id: this.userInfo?.user_id,
          type: "sms",
        }
      )
      .pipe(catchError((error) => of({ error })))
      .subscribe((response) => {
        const message =
          response?.$metadata?.httpStatusCode === 200
            ? "Successfully Verified for MFA with Authenticator App"
            : "Unknown Error Updating for MFA with Authenticator App Preference";

        this.notifications.popNotification("", message);

        this.smsVerified = true;
        if (this.totbVerified && this.smsVerified) {
          this.dialogRef.close();
        }
        this.view = "options";
      });
  }

  async handleSuccessfulTotbVerifyResponse() {
    this.http
      .request(
        "post",
        `${this.environmentService.environment.apiRoot}/auth/updatemfa`,
        {
          username: this.userInfo?.cognito_username,
          id: this.userInfo?.user_id,
          type: "totb",
        }
      )
      .pipe(catchError((error) => of({ error })))
      .subscribe((response) => {
        const message =
          response?.$metadata?.httpStatusCode === 200
            ? "Successfully Verified for MFA with Authenticator App"
            : "Unknown Error Updating for MFA with Authenticator App Preference";

        this.notifications.popNotification("", message);

        this.totbVerified = true;
        if (this.totbVerified && this.smsVerified) {
          this.dialogRef.close();
        }
        this.view = "options";
      });
  }

  handleUnsuccessfulVerifyResponse(response) {
    if (response.error === "CodeMismatchException")
      return this.notifications.popNotification(
        "",
        "Error with Code Provided. Please try again."
      );
    switch (response?.error?.name) {
      case "CodeMismatchException":
      case "EnableSoftwareTokenMFAException":
      case "InvalidParameterException": {
        return this.notifications.popNotification(
          "",
          "Error with Code Provided. Please try again."
        );
      }

      default: {
        this.notifications.popNotification("", "Unknown Error");
      }
    }
  }

  submitSmsCode($event) {
    $event?.preventDefault();
    this.isSubmitting = true;
    this.userService
      .sendVerficationCode("phone_number", this.smsCode)
      .pipe(catchError((error) => of({ error })))
      .subscribe((response) => {
        this.isSubmitting = false;
        response.status === "verification sent"
          ? this.handleSuccessfulSmsVerifyResponse()
          : this.handleUnsuccessfulVerifyResponse(response);
      });
  }

  submitTotbUsercode($event) {
    $event?.preventDefault();
    this.isSubmitting = true;
    this.http
      .request(
        "post",
        `${this.environmentService.environment.apiRoot}/auth/verifytotbmfa`,
        JSON.stringify({ usercode: this.totbCode })
      )
      .pipe(catchError((error) => of({ error })))
      .subscribe((response) => {
        this.isSubmitting = false;
        response.Status === "SUCCESS"
          ? this.handleSuccessfulTotbVerifyResponse()
          : this.handleUnsuccessfulVerifyResponse(response);
      });
  }
}
